import { fetchValhallaOptimizedRoutePlan } from "@/api/tripPlanning/calls/valhallaCalls/valhallaOptimizeRouteCalls";
import { fetchValhallaLongTrip } from "@/api/tripPlanning/calls/valhallaCalls/valhallaRoutePlanningCalls";
import type {
  Valhalla_CostingModel,
  Valhalla_Location,
  Valhalla_Trip,
  ValhallaErrorCode,
  ValhallaRouteFailure,
  ValhallaRouteResult,
} from "@/api/tripPlanning/types/valhallaTypes";
import {
  createTrip,
  updateTrip,
  type TripDTO,
} from "@/api/vtnz-backend/trips";
import type { Vehicle } from "@/domains/vehicles";
import generateUniqueLocalID from "@/utils/generateUniqueLocalID";
import { TripICE, type Itinerary } from "..";
import type { Trip, TripPlanningReturn, TripStatus, TripType } from "../types";
import type { TripLocationCore } from "./tripLocationCoreClass";

/** The trip-type-specific portion of a route plan's persisted columns. */
export type TripCoreDTO = {
  itinerary: unknown;
  planning_parameters: unknown;
};

export type TripCoreOptions = {
  /** the optional external id for the trip */
  externalId?: string;
  /** the type of the trip */
  type: TripType;
  /** the status of the trip */
  status?: TripStatus;
  /** the optional display name for the trip */
  name?: string;
  /** the locations that make up the trip */
  locations?: TripLocationCore[];
  /** the itinerary for the trip */
  itinerary: Itinerary;
  /** the optional external id for the vehicle associated with the trip */
  vehicleExternalId?: string | null;
  /** the optional vehicle associated with the trip */
  vehicle?: Vehicle | null;
  /** Initial starting load for this trip in kilograms. */
  startingLoad?: number;
};

export abstract class TripCore {
  // -------------------------------------------------------------------- //
  // ------------------------- Global class state ----------------------- //
  // -------------------------------------------------------------------- //

  // global record of class instance ids this session.
  static usedIds: string[] = [];

  // -------------------------------------------------------------------- //
  // ------------------------------- State ------------------------------ //
  // -------------------------------------------------------------------- //

  readonly id: string;
  externalId?: string;
  readonly type: TripType;
  status: TripStatus = "unplanned";
  locations: TripLocationCore[] = [];
  itinerary: Itinerary;
  /** the optional display name for the trip */
  name: string | null;
  /** the optional external id for the vehicle associated with the trip */
  vehicleExternalId: string | null;
  /** the optional vehicle associated with the trip */
  vehicle: Vehicle | null;
  /** Initial starting load for this trip in kilograms. */
  startingLoad: number;
  planningFailureReason: string | null = null;

  // -------------------------------------------------------------------- //
  // ---------------------------- Constructor --------------------------- //
  // -------------------------------------------------------------------- //

  constructor(options: TripCoreOptions) {
    this.id = generateUniqueLocalID(TripCore.usedIds, "trip");
    this.type = options.type;
    this.status = options.status ?? "unplanned";
    this.name = options.name ?? null;
    this.externalId = options.externalId;
    this.itinerary = options.itinerary;
    this.locations = options.locations ?? [];
    this.vehicleExternalId = options.vehicleExternalId ?? null;
    this.vehicle = options.vehicle ?? null;
    this.startingLoad = options.startingLoad ?? 0;

    TripCore.usedIds.push(this.id);
  }

  static fromDTO(dto: TripDTO, vehicle: Vehicle | undefined): Trip {
    switch (vehicle?.fuelType) {
      case "DIESEL":
      case "PETROL":
      case "HYBRID":
        return TripICE.fromDTO(dto, vehicle);
      case "ELECTRIC":
      case "HYDROGEN":
      case "PHEV":
      case "OTHER":
        throw new Error("Unsupported trip type");
      default:
        return TripICE.fromDTO(dto, undefined);
    }
  }

  // ----------------------------------------------------------------------- //
  // -------------------------------- Getters ------------------------------ //
  // ----------------------------------------------------------------------- //

  /**
   * The origin location of the trip.
   *
   * @return {TripLocation | undefined} The origin location of the trip, or undefined if the trip has no locations.
   */
  public get origin(): TripLocationCore | undefined {
    return this.locations[0];
  }

  /**
   * Returns the destination location of the trip.
   *
   * @return {TripLocation | undefined} The destination location, or undefined if no locations are available.
   */
  public get destination(): TripLocationCore | undefined {
    return this.locations[this.locations.length - 1];
  }

  /**
   * Returns an array of waypoints in the trip. The origin and destination are excluded from the array.
   *
   * @return {TripLocation[]} The waypoints of the trip.
   */
  public get waypoints(): TripLocationCore[] {
    if (this.locations.length <= 2) return []; // no waypoints
    return this.locations.slice(1, -1); // exclude origin and destination
  }

  // ----------------------------------------------------------------------- //
  // -------------------------------- Methods ------------------------------ //
  // ----------------------------------------------------------------------- //

  /**
   * Compiles the trip-type-specific portion (`itinerary`/`planning_parameters`)
   * of this trip's persisted route plan row. Not meant to be called directly
   * (only by `saveTrip` below) - would ideally be `protected`, but see
   * `onWaypointsOptimised`'s doc comment for why that breaks `Trip[]` type
   * inference in `tripStore.ts`.
   */
  public abstract compileDTO(): TripCoreDTO;

  /**
   * Persists this trip to the backend: creates a new route plan if it
   * hasn't been saved before, or updates the existing one otherwise.
   *
   * `fleetId`/`currentUserId` come from `fleetStore`/`userStore` rather than
   * being read here directly - trip domain classes don't reach into Pinia
   * stores (mirrors `tripStore.fetchTrips` taking `vehicles` as a param
   * instead of calling `useVehicleStore()` itself). The current user is
   * assumed to be the assigned driver, since there's no driver-assignment
   * UI yet. `created_by`/`assigned_driver_id` do have a `current_user_id()`
   * database default on paper, but it comes back `null` in practice on this
   * table (confirmed live), so `currentUserId` is sent explicitly rather
   * than left for the database to fill in (see `TripCreateDTO`).
   */
  public async saveTrip({
    fleetId,
    currentUserId,
  }: {
    fleetId: string;
    currentUserId: string;
  }): Promise<{ success: boolean; error?: string }> {
    if (!this.vehicleExternalId) {
      return {
        success: false,
        error: "This trip has no vehicle assigned and cannot be saved.",
      };
    }

    const { itinerary, planning_parameters } = this.compileDTO();

    if (this.externalId) {
      const result = await updateTrip(this.externalId, {
        name: this.name ?? undefined,
        itinerary,
        planning_parameters,
        vehicle_id: this.vehicleExternalId,
      });

      if (!result.success) {
        return { success: false, error: result.errorMsg };
      }

      return { success: true };
    }

    const result = await createTrip({
      name: this.name ?? undefined,
      itinerary,
      planning_parameters,
      vehicle_id: this.vehicleExternalId,
      fleet_id: fleetId,
      created_by: currentUserId,
      assigned_driver_id: currentUserId,
      // PoC placeholder: there's no trip-date concept in the planning flow
      // yet, so a route plan is dated to when it was saved.
      date: new Date().toISOString(),
    });

    if (!result.success) {
      return { success: false, error: result.errorMsg };
    }

    this.externalId = result.data.id;

    return { success: true };
  }

  public async fetchValhallaTripData(
    vehicle_data?: Vehicle,
  ): Promise<
    | { success: true; tripData: Valhalla_Trip }
    | { success: false; error: string }
  > {
    try {
      const vehicle = vehicle_data ?? this.vehicle;

      const locations = this.compileValhallaLocations();

      const costing: Valhalla_CostingModel =
        vehicle?.valhallaCostingType ?? "auto";

      const result = await fetchValhallaLongTrip({
        locations,
        costing,
      });

      if (!result.success) {
        return {
          success: false,
          error: this.getFriendlyValhallaError(result),
        };
      }

      return {
        success: true,
        tripData: result.route.trip,
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Unknown error",
      };
    }
  }

  /**
   * Compiles the Valhalla locations based on whether the trip is a round trip or not.
   *
   * @return {Valhalla_Location[]} An array of Valhalla_Location objects representing the compiled locations.
   */
  public compileValhallaLocations(): Valhalla_Location[] {
    return this.locations.map(
      (location) => location.coordinates.asAbbreviatedObj,
    );
  }

  /**
   * Returns a user-friendly message for a failed Valhalla route planning request.
   *
   * Groups common Valhalla error codes into categories with consistent,
   * human-readable messages. Unknown error codes fall back to the original
   * message returned by the Valhalla API.
   *
   * @param valhallaErrorRes The failed Valhalla route planning result.
   * @returns A display message suitable for presenting to the user.
   */
  public getFriendlyValhallaError(
    valhallaErrorRes: ValhallaRouteFailure,
  ): string {
    // TODO: find a better place for the category data
    const VALHALLA_ERROR_CATEGORIES: {
      codes: ValhallaErrorCode[];
      message: string;
    }[] = [
      {
        codes: [441, 442],
        message:
          "We are unable to plan a trip with the locations and vehicle provided. Please review them and try again.",
      },
      {
        codes: [170],
        message:
          "The locations you have selected are in unconnected regions, and no ferry service could be found to connect them.",
      },
      {
        codes: [110, 412],
        message:
          "The trip data appears to be invalid. Please review it and try again.",
      },
      {
        codes: [154, 314, 400],
        message:
          "This trip is too complex to plan as a single trip. Consider splitting it into smaller trips and trying again.",
      },
    ];

    const errorCode = valhallaErrorRes.error.error_code;

    const category = VALHALLA_ERROR_CATEGORIES.find(({ codes }) =>
      codes.includes(errorCode),
    );

    return category?.message ?? valhallaErrorRes.error.error;
  }

  public abstract changeVehicle(vehicle: Vehicle | null): Promise<void>;

  public abstract planTrip(options: {
    forceReCalc: boolean;
  }): Promise<TripPlanningReturn>;

  /**
   * Fetches the optimized (a trip the reorders waypoints to solve the
   * travelling salesman problem) trip from the Valhalla route endpoint.
   *
   * @return {Promise<ValhallaRouteResult>} A Promise that resolves to the optimized trip or void.
   */
  public async fetchValhallaOptimizedTrip(): Promise<ValhallaRouteResult> {
    const costing: Valhalla_CostingModel | undefined =
      this.vehicle?.valhallaCostingType;
    const res = await fetchValhallaOptimizedRoutePlan(
      this.compileValhallaLocations(),
      costing,
    );
    return res;
  }

  /**
   * Reorders this trip's waypoints to solve the travelling salesman problem,
   * then hands off to `onWaypointsOptimised` for whatever needs to happen
   * between getting the optimised route back from Valhalla and generating
   * the final itinerary (e.g. an EV trip re-checking energy/charging
   * feasibility against the reordered stops) — that step is vehicle-type
   * specific, so it's left to each subclass.
   */
  public async optimiseWaypoints(): Promise<TripPlanningReturn> {
    if (this.locations.length < 3) {
      return {
        success: false,
        error: "Trip has too few stops to optimise.",
      };
    }

    const valhallaRes = await this.fetchValhallaOptimizedTrip();

    if (!valhallaRes.success) {
      return {
        success: false,
        error: this.getFriendlyValhallaError(valhallaRes),
      };
    }

    // check if order has changed
    const originalIndexes = valhallaRes.route.trip.locations.map(
      (location) => location.original_index,
    );
    const newIndexes = valhallaRes.route.trip.locations.map(
      (location, index) => index,
    );
    if (
      newIndexes.some((newIndex, index) => newIndex !== originalIndexes[index])
    ) {
      // reorder locations based on res.
      const newLocations = newIndexes.map(
        (newIndex) => this.locations[originalIndexes[newIndex]!],
      ) as TripLocationCore[];
      this.locations = newLocations;
    }

    return this.onWaypointsOptimised(valhallaRes);
  }

  /**
   * Runs after waypoints have been reordered by `optimiseWaypoints`, and
   * before the trip is considered re-planned. This is where a subclass
   * performs its own vehicle-type-specific knock-on effects (e.g. an EV
   * trip re-checking energy/charging feasibility) and builds/assigns its
   * own itinerary type from the result.
   *
   * Not meant to be called directly (only by `optimiseWaypoints` above) -
   * would ideally be `protected`, but a `protected abstract` member here
   * breaks `Trip[]` type inference in `tripStore.ts` through Pinia/Vue's
   * reactivity type wrapping, so it's `public` instead.
   */
  public abstract onWaypointsOptimised(
    valhallaRes: ValhallaRouteResult & { success: true },
  ): Promise<TripPlanningReturn>;

  // --- Location Manipulation Methods --- //

  /**
   * Updates an existing location in the trip's list of locations.
   *
   * @param {TripLocation} location - The location to update.
   * @return {void} This function does not return anything.
   */
  public updateExistingLocation(location: TripLocationCore): void {
    const index = this.locations.findIndex((loc) => loc.id === location.id);
    if (index !== -1) {
      this.locations[index] = location;
    }
  }
}
